import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import styles from "../css/EditNote.module.css"
import { colors_dict } from '../../../data/colorsDict'
import TagMenu from '../../board/js/TagMenu'
import ColorMenu from './ColorMenu'
import LoadingButton from '../../Loaders/js/LoadingButton'
import NotFound from '../../Error/js/NotFound';
import CrashError from '../../Error/js/CrashError';
import { useDispatch, useSelector } from 'react-redux';
import { editNote, viewNote } from '../../../actions/noteActions'
import NoteEditor from './NoteEditor';
import { useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import CodeBlock from '@tiptap/extension-code-block'
import { BsPlus } from 'react-icons/bs'
import { PageContext } from '../../board/js/Notes'
import useFormattedTime from '../../../hooks/useFormattedTime'
import { FaFeather } from 'react-icons/fa6'
import { FiX } from 'react-icons/fi'
import CharacterCount from '@tiptap/extension-character-count'
import Placeholder from '@tiptap/extension-placeholder'
import { TAG_RESET } from '../../../actions/tagTypes'
import UseMobileLayout from '../../../hooks/useMobileLayout'

const EditNote = () => {
    const [pageModals,setPageModals] = useContext(PageContext)
    const { edit: id} = pageModals
    const [errors,setErrors] = useState({})
    const [showTags,setShowTags] = useState(false)
    const [showColors,setShowColors] = useState(false)
    const textRef = useRef(null)
    const mounted = useRef(true)
    const initializedRef = useRef(false)
    const noteSet = useRef(false)
    const loading = useSelector((state)=> state.noteState.loading)
    const note = useSelector((state)=>state.noteState.selectedNote)
    const [updatedNote, setUpdatedNote] = useState(note)
    const updateDate = useFormattedTime(note?.updatedAt)
    const crashError = useSelector((state)=>state.noteState.crashError)
    const notFound = useSelector((state)=>state.noteState.notFound)
    const dispatch = useDispatch()
    UseMobileLayout()

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                codeBlock: false,
                heading: { levels: [1, 2, 3] },
                horizontalRule: true,
            }),
            CodeBlock.configure({
                HTMLAttributes: {
                    class: 'ProsemirrorEditor-codeBlock',
                },
            }),
            Underline,
            Placeholder.configure({
                placeholder: 'Start writing...',
            }),
            CharacterCount
        ],
        content: updatedNote?.content,
        onUpdate: ({ editor }) => {
            if (editor.storage.characterCount.characters() > 25 && errors.content === "Content must be at least 25 characters"){
                setErrors((prevErrors)=>{
                    let copy = {...prevErrors}
                    delete copy.content
                    return copy
                })
            }
            setUpdatedNote({...updatedNote,content:editor.getHTML()})
        },
        editorProps: {
            transformPastedHTML(html) {
                const parser = new DOMParser()
                const doc = parser.parseFromString(html, 'text/html')
                const allowedTags = new Set([
                    'B', 'STRONG', 'I', 'EM', 'U', 'S',
                    'H1', 'H2', 'H3',
                    'UL', 'OL', 'LI',
                    'CODE', 'PRE', 'HR',
                    'P', 'BR', 'A'
                ])
                function clean(node) {
                    for (const child of [...node.childNodes]) {
                        if (child.nodeType === Node.ELEMENT_NODE) {
                            if (!allowedTags.has(child.tagName)) {
                                child.replaceWith(...child.childNodes)
                            } else {
                                [...child.attributes].forEach(attr => child.removeAttribute(attr.name))
                                clean(child)
                            }
                        }
                    }
                }

                clean(doc.body)
                return doc.body.innerHTML
            },
            handleKeyDown: (_, event) => {
                if (event.key === 'Enter' && editor.isEmpty) {
                    event.preventDefault()
                    editor.chain().focus().setHardBreak().run()
                    return true
                }
            }
        }
    })

    useEffect(()=>{
        if (mounted.current){
            mounted.current = false
            dispatch(viewNote(id))
        }
    },[id,dispatch])

    useEffect(() => {
        if (!noteSet.current && note){
            noteSet.current = true
            setUpdatedNote(JSON.parse(JSON.stringify(note)))
        }
    }, [note])


    useEffect(() => {
        if (editor && updatedNote?.content && !initializedRef.current) {
            editor.commands.setContent(updatedNote.content, false)
            initializedRef.current = true
        }
    }, [editor, updatedNote?.content])

    const handleChange = (e) => {
        let name = e.target.name
        let value = e.target.value
        if (name === "title"){
            value = value.replace(/\s\s+/g, ' ')
        }
        setUpdatedNote({...updatedNote,[name]:value})
        if (errors[name]){
            setErrors((prevErrors)=>{
              let copy = {...prevErrors}
              delete copy[name]
              return copy
            })
        }
    }

    const closeModal = () => {
        setPageModals((prevState)=>{
            let copy = {...prevState}
            copy.edit = null
            return copy
        })
    }

    const saveChanges = async() => {
        try{
            await dispatch(editNote({
                title: updatedNote.title.trim(),
                content: updatedNote.content,
                tags: updatedNote.tags,
                color: updatedNote.color
            },id))
            setPageModals({...pageModals,edit:null})
        }
        catch(err){
            if (err.response?.status === 400){
                setErrors({...errors,...err.response.data})
            }
        }
    }

    const handleSubmit = async(e) => {
        e.preventDefault()
        document.activeElement.blur()
        let valid = true
        if (updatedNote.title.trim().length < 2){
            valid = false
            setErrors((prevErrors)=> {return {...prevErrors,title:"Title must be at least 2 characters"}})
        }
        if (editor.storage.characterCount.characters() < 25){
            valid = false
            setErrors((prevErrors)=> {return {...prevErrors,content:"Content must be at least 25 characters"}})
        }
        if (valid){
            await saveChanges()
        }
    }

    const removeTag = (index) => {
        let tags = [...updatedNote.tags]
        tags.splice(index,1)
        setUpdatedNote({...updatedNote,tags:tags})
    }

    const isDisabled = useMemo(()=>{
        if (!updatedNote?.title || !updatedNote?.color || !updatedNote?.content){
            return true
        }
        if (Object.keys(errors).length){
            return true
        }
    },[updatedNote,errors])

    const handleKeyDown = (e) => {
        const isPrintableCharacter = e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey
        if (isPrintableCharacter){
            if (updatedNote.title.length === 30){
                e.preventDefault()
                e.target.classList.add(styles.shake)
                setTimeout(()=>{
                    e.target.classList.remove(styles.shake)
                },300)
            }
        }
    }

    const deleteTag = (tag_id) => {
        setUpdatedNote((prevState)=>{
            let tags = [...prevState.tags]
            tags = tags.filter(tag => tag._id !== tag_id)
            return {...prevState,tags:tags}
        })
    }

    const handleScroll = (isIos = false) => {
        if (showColors) setShowColors(false)
        if (isIos && showTags){
            setShowTags(false)
        }
    }

    const openTagMenu = () => {
        dispatch({type: TAG_RESET})
        setShowTags(true)
    }

    const closeTagMenu = () => {
        setShowTags(false)
        dispatch({type: TAG_RESET})
    }

    const handleTagMenu = () => {
        if (showTags){
            closeTagMenu()
        }
        else{
            openTagMenu()
        }
    }

    return (
        notFound.state ? 
            <NotFound
                msg = {notFound.text}
                closeMenu = {()=>closeModal()}
            /> 
            :
        crashError.state ?
            <CrashError
                closeMenu = {()=>closeModal()}
                msg = {crashError.text}
                retry = {()=> (updatedNote ? saveChanges() : dispatch(viewNote(id)))}
            /> 
            : 
        updatedNote ? <div className = {`${styles.wrapper} modal-wrapper`} onScroll = {()=>handleScroll("ios-mobile")}>
            <div className = {`${styles.innerWrapper} inner-wrapper`}>
                <form data-testid = {`edit-modal-${id}`} className= {`${styles.modal} nested-container`} onSubmit = {handleSubmit}>
                    <div className={styles.header}>
                        <div className={styles.headerLeft}>
                            <div className = {styles.icon}>
                                <div className = {styles.feathers}>
                                    <FaFeather className={styles.featherShadow}/>
                                    <FaFeather className={styles.featherMain}/>
                                </div>
                            </div>
                            <div className={styles.title}>Edit Note</div>
                        </div>
                        <button data-testid = "close-btn" type = "button" className={styles.closeBtn} onClick = {()=>closeModal()}><FiX/></button>
                    </div>
                    <div className = {`${styles.body} nested-container`} onScroll={()=>handleScroll()}>
                        <div className={styles.colorRow}>
                            <button data-testid = "color-btn" type = "button" className={styles.colorPill} onClick = {()=>setShowColors(!showColors)}>
                                <div className={styles.colorPillDot} style = {{ backgroundColor: colors_dict[updatedNote.color] }}></div>
                                <div className={styles.colorPillLabel}>{updatedNote.color}</div>
                            </button>
                            {showColors && 
                                <ColorMenu
                                    selectColor={(color)=>setUpdatedNote({...updatedNote,color:color})}
                                    selectedColor={updatedNote.color}
                                    closeMenu = {()=>setShowColors(false)}
                                />
                            }
                        </div>
                        <div className = {styles.inputArea}>
                            {errors.title && <div data-testid = "title-error" className = {styles.error}>{errors.title}</div>}
                            <input
                                className = {`${styles.titleInput} ${errors.title ? styles.errorInput : ""}`}
                                type = "text"
                                name = "title"
                                value = {updatedNote.title}
                                onChange = {handleChange}
                                onKeyDown = {handleKeyDown}
                                placeholder = "Note title…"
                            />
                            <div className = {styles.length}>{updatedNote.title.length}/30</div>
                        </div>
                        <div className = {styles.inputArea}>
                            {errors.content && <div data-testid = "content-error" className = {styles.error}>{errors.content}</div>}
                            <div className = {`${styles.editorWrap} ${errors.content ? styles.errorInput : ""}`} ref = {textRef}>
                                <NoteEditor editor = {editor} editing = {true}/>
                            </div>
                        </div>
                    </div>
                    <div className={styles.tagsRow}>
                        {updatedNote.tags.map((tag,index)=>(
                            <div data-testid = {`note-selected-tag-${tag._id}`} className = {styles.tag} key = {index}>
                                {tag.name}<button data-testid = "tag-delete-btn" type = "button" className={styles.tagRemove} onClick = {()=>removeTag(index)}>×</button>
                            </div>
                        ))}
                        {updatedNote.tags.length < 5 && <div className = {styles.btnWrapper}>
                            {updatedNote.tags.length ? 
                                <button data-testid = "tag-btn" type = "button" className = {`${styles.addTag} ${showTags ? styles.active : ""}`} onClick = {()=>handleTagMenu()}><BsPlus/></button>
                                : <button data-testid = "tag-btn" type = "button" className = {`${styles.tagBtn} ${showTags ? styles.active : ""}`} onClick = {()=>handleTagMenu()}>+ Add Tag</button>
                            }
                            {showTags && 
                                <TagMenu
                                    selectedTags = {updatedNote.tags}
                                    addTag = {(tag) => {
                                        setUpdatedNote(prev => {
                                            const updatedTags = [...prev.tags, tag]
                                            if (updatedTags.length === 5) {
                                                setShowTags(false)
                                            }
                                            return { ...prev, tags: updatedTags }
                                        })
                                    }}
                                    removeTag = {deleteTag}
                                    closeMenu = {()=>closeTagMenu()}
                                    modal = "edit-note"
                                />
                            }
                        </div>}
                    </div>
                    <div className={styles.footer}>
                        <div className={styles.lastUpdated}>Last updated: {updateDate}</div>
                        <button type = "submit" className = {`${styles.saveBtn} ${loading ? styles.loading : ""}`} disabled = {isDisabled}>
                            Save Changes <div className={styles.saveIcon}>→</div>
                            {loading && <LoadingButton/>}
                        </button>
                    </div>
                </form>
            </div>
        </div> :
         <div className = {styles.wrapper}>
            <div className = {styles.innerWrapper}>
                <div className = {styles.modal}>
                    <div className={styles.header}>
                        <div className={styles.headerLeft}>
                            <div className = {styles.icon}>
                                <div className = {styles.feathers}>
                                    <FaFeather className={styles.featherShadow}/>
                                    <FaFeather className={styles.featherMain}/>
                                </div>
                            </div>
                            <div className={styles.title}>Edit Note</div>
                        </div>
                        <button type = "button" className={styles.closeBtn} onClick = {()=>closeModal()}><FiX/></button>
                    </div>
                    <div className = {styles.content}>
                        <div className = {styles.loadingArea}>
                            {[...Array(6).keys()].map((_,index)=>(
                                <div className = {styles.bar} key = {index}></div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default EditNote